import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

import { getAemContentPolarisTabCollectionQuery } from '../queries';
import { DtlTabVariables, DTLUxlResponse } from './types';

export function getUxlVariablesForDTL(tabTitles: string | string[], region?: string): DtlTabVariables {
  // Convert single tab to array for uniform handling
  const tabs = Array.isArray(tabTitles) ? tabTitles : [tabTitles];

  return {
    input: {
      queryName: 'tabcollectionlistvariant',
      // Geo-targeting: the resolved region travels as the first-class `variation`
      // input (DPO-87062 / WEB-207197). Sent only when a region resolved; the key is
      // omitted (not null/empty) otherwise so AEM serves the `master` copy.
      ...(region ? { variation: region } : {}),
      filters: [
        {
          name: 'tags',
          expressions: tabs.map(tab => ({
            value: tab.toLowerCase(),
            operator: 'EQUALS',
          })),
        },
      ],
    },
  };
}

export function useGetUxlForDTL(): (tabTitles: string | string[], region?: string) => Promise<DTLUxlResponse | null> {
  const client = useApolloClient();
  const requestId = useClientEnvVarsStore(state => state.envVarsObject?.['REQUEST_ID']);

  return useCallback(
    async (tabTitles: string | string[], region?: string) => {
      try {
        const result = await client.query<DTLUxlResponse, DtlTabVariables>({
          query: getAemContentPolarisTabCollectionQuery,
          variables: getUxlVariablesForDTL(tabTitles, region),
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
          context: {
            headers: {
              ...(requestId && { 'x-request-id': requestId }),
            },
          },
        });
        const { data, error } = result;
        if (error) {
          console.error('[useGetUxlForDTL] Error calling Apollo query for DTL operation:', error);
          return null;
        }
        return data ?? null;
      } catch (error) {
        console.error('[useGetUxlForDTL] Error calling Apollo query for DTL operation:', error);
        return null;
      }
    },
    [client]
  );
}
